import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import ptmService from '../../services/ptmService';
import DialogModal from '../../components/DialogModal';
import { CalendarPlus, Trash, X } from '@phosphor-icons/react';

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function TeacherPTMSection() {
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({ date: todayISO(), start_time: '15:00', end_time: '15:20', location: '' });
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, type, confirmLabel, onConfirm }

  const loadAll = async () => {
    try {
      setLoading(true);
      const [slotsData, bookingsData] = await Promise.all([
        ptmService.listMySlots(todayISO()),
        ptmService.listMyTeacherBookings(),
      ]);
      setSlots(slotsData.slots || []);
      setBookings((bookingsData.bookings || []).filter((b) => b.status === 'confirmed'));
    } catch (err) {
      toast.error(err.message || 'Failed to load meeting slots');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleCreateSlot = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await ptmService.createSlot(form);
      toast.success('Slot published');
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Failed to create slot');
    } finally {
      setCreating(false);
    }
  };

  const handleCancelSlot = async (slotId) => {
    setBusyId(slotId);
    try {
      await ptmService.cancelSlot(slotId);
      toast.success('Slot cancelled');
      loadAll();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel slot');
    } finally {
      setBusyId(null);
    }
  };

  const handleCancelBooking = (bookingId) => {
    setConfirmDialog({
      title: 'Cancel Meeting',
      message: 'Cancel this meeting? The parent will be notified.',
      type: 'warning',
      confirmLabel: 'Cancel Meeting',
      onConfirm: async () => {
        setBusyId(bookingId);
        try {
          await ptmService.cancelBookingAsTeacher(bookingId);
          toast.success('Meeting cancelled');
          loadAll();
        } catch (err) {
          toast.error(err.message || 'Failed to cancel meeting');
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  return (
    <div className="teacher-ptm-section">
      <div className="form-card" style={{ padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Publish a meeting slot</h3>
        <form onSubmit={handleCreateSlot} className="form-row" style={{ flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div className="form-group" style={{ minWidth: 150 }}>
            <label>Date</label>
            <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div className="form-group" style={{ minWidth: 120 }}>
            <label>Start</label>
            <input type="time" required value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
          </div>
          <div className="form-group" style={{ minWidth: 120 }}>
            <label>End</label>
            <input type="time" required value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
          </div>
          <div className="form-group" style={{ minWidth: 160 }}>
            <label>Location (optional)</label>
            <input type="text" placeholder="e.g. Staff Room 2 / Video call" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div className="form-group">
            <button className="btn btn-primary" type="submit" disabled={creating}>
              <CalendarPlus size={16} /> {creating ? 'Publishing...' : 'Publish Slot'}
            </button>
          </div>
        </form>
      </div>

      <div className="form-card" style={{ padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Upcoming meetings</h3>
        {loading ? (
          <p>Loading...</p>
        ) : bookings.length === 0 ? (
          <p style={{ fontSize: 13, color: '#6b7280' }}>No meetings booked yet.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr><th>Date</th><th>Time</th><th>Parent</th><th>Student</th><th>Purpose</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.booking_id}>
                    <td>{b.date}</td>
                    <td>{b.start_time}-{b.end_time}</td>
                    <td>{b.parent_name}</td>
                    <td>{b.student_name}</td>
                    <td>{b.purpose || '—'}</td>
                    <td>
                      <button className="btn-icon" title="Cancel" disabled={busyId === b.booking_id} onClick={() => handleCancelBooking(b.booking_id)}>
                        <X size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="form-card" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Your published slots</h3>
        {loading ? (
          <p>Loading...</p>
        ) : slots.length === 0 ? (
          <p style={{ fontSize: 13, color: '#6b7280' }}>No upcoming slots published.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr><th>Date</th><th>Time</th><th>Location</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {slots.map((s) => (
                  <tr key={s.id}>
                    <td>{s.date}</td>
                    <td>{s.start_time}-{s.end_time}</td>
                    <td>{s.location || '—'}</td>
                    <td className="capitalize">{s.status}</td>
                    <td>
                      {s.status === 'open' && (
                        <button className="btn-icon" title="Cancel slot" disabled={busyId === s.id} onClick={() => handleCancelSlot(s.id)}>
                          <Trash size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DialogModal
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type || 'warning'}
        primaryAction={{ label: confirmDialog?.confirmLabel || 'Confirm', onClick: () => confirmDialog?.onConfirm() }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );
}

export default TeacherPTMSection;
